import React from "react";
import Marquee from "react-fast-marquee";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

const GAME_IFRAME_URL =
  "https://s.winabc.xyz/widgets/table652Mini/table652Mini/";

import BackgroundDesktop from "assets/img-winaz/xoc-dia-landing/bg.webp";
import LogoWinaz from "assets/img-winaz/xoc-dia-landing/logo.webp";
import Lantern from "assets/img-winaz/xoc-dia-landing/denlong.webp";
import HeadlineImg from "assets/img-winaz/xoc-dia-landing/dknhancode.webp";
import GameLabelBg from "assets/img-winaz/xoc-dia-landing/khungthongbaogamelive.webp";
import HeroImg from "assets/img-winaz/xoc-dia-landing/khunglive.webp";
import LiveNoticeBg from "assets/img-winaz/xoc-dia-landing/khaygame xoc.webp";
import LiveBadge from "assets/img-winaz/xoc-dia-landing/livelabel.webp";
import CtaPlayButton from "assets/img-winaz/xoc-dia-landing/btn_lamvaitay.webm";
import FeatureCard1 from "assets/img-winaz/xoc-dia-landing/block1.webp";
import FeatureCard2 from "assets/img-winaz/xoc-dia-landing/block2.webp";
import FeatureCard3 from "assets/img-winaz/xoc-dia-landing/block3.webp";
import SideLabelLeft from "assets/img-winaz/xoc-dia-landing/banner1.webp";
import SideLabelRight from "assets/img-winaz/xoc-dia-landing/banner2.webp";
import IconFacebook from "assets/img-winaz/xoc-dia-landing/fb.webp";
import IconTelegram from "assets/img-winaz/xoc-dia-landing/tele.webp";
import LogoGalaxy from "assets/img-winaz/logogalaxy.webp";

import GameStageHeadline from "assets/img-winaz/xoc-dia-landing/typo1.webp";
import OnlineBadgeBg from "assets/img-winaz/xoc-dia-landing/khungsonguoi.webp";
import ProofBadgeBg from "assets/img-winaz/xoc-dia-landing/noti.webp";

// Scroll đỏ trơn (không có chữ sẵn) — đúng khung của modal "CHÚC MỪNG BẠN".
// khungthongbaogamelive.png (dùng cho badge "Game chơi thử") là 1 thanh
// pill mỏng, không phải hình khung scroll này.
import ScrollBg from "assets/img-winaz/xoc-dia-landing/bgpopupthangphien.webp";
import GiftIcon from "assets/img-winaz/xoc-dia-landing/iconqua.webp";
import GiftIconBg from "assets/img-winaz/xoc-dia-landing/khungqua.webp";
import BtnNhanThuong from "assets/img-winaz/xoc-dia-landing/btnnhanthuong.webp";

import InputBg from "assets/img-winaz/xoc-dia-landing/input.webp";
import RewardSound from "assets/sounds/result_and_reward.mp3";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconPassword from "assets/img-winaz/signup-form/iconpassword.webp";

import { createMyEventClient, getToken } from "transport";
import { getDeviceFingerprint } from "utils/device";
import { useMyStore } from "store";
import { LandingXDLiveStatus } from "@marketplace/myevent-connect/model_pb";
import { SOCIAL_LINKS } from "const";
import {
  TICKER_ITEMS,
  GAME_LABEL_PREFIX,
  GAME_LABEL_VALUE,
  LIVE_NOTICE_TEXT,
  SUBTEXT_LINE_1,
  SUBTEXT_LINE_2_PREFIX,
  SUBTEXT_LINE_2_HIGHLIGHT,
  SIDE_LABEL_LEFT,
  SIDE_LABEL_RIGHT,
  FEATURE_CARDS_ALT,
  GAME_STAGE_HEADLINE_ALT,
  ONLINE_BADGE_SUFFIX,
  REWARD_TITLE,
  REWARD_ITEM_LABEL,
  REWARD_ITEM_SUBLABEL,
  REWARD_CLAIM_WINDOW_SECONDS,
} from "./constants";
import { useOnlineCount } from "./useOnlineCount";
import { useProofNoti } from "./useProofNoti";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

const INTERCOM_APP_ID = "snx821c4";
type IntercomModule = typeof import("@intercom/messenger-js-sdk");

// Giữ nguyên hoàn toàn từ Home-Winaz-Tan-Thu.
const INTERACTIVE_IMAGE_PROPS = {
  whileHover: { filter: "brightness(1.08)" },
  whileTap: { filter: "brightness(0.92)" },
};

type WizardStep = "intro" | "playing";

const formatCountdown = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
};

const formatCash = (cash: string | null) => {
  if (!cash) return "29.000";
  const n = parseInt(cash, 10);
  if (isNaN(n)) return cash;
  return n.toLocaleString("vi-VN").replace(/,/g, ".");
};

const STARTING_BALANCE_RAW = 100_000_000;


const Home: React.FunctionComponent = () => {
  const [step, setStep] = React.useState<WizardStep>("intro");
  const onlineCount = useOnlineCount();
  const activeProof = useProofNoti();
  const [isRewardModalOpen, setIsRewardModalOpen] = React.useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = React.useState(false);
  const [claimSecondsLeft, setClaimSecondsLeft] = React.useState(
    REWARD_CLAIM_WINDOW_SECONDS,
  );
  const [rewardCash, setRewardCash] = React.useState<string | null>(null);
  const [finalBalanceCash, setFinalBalanceCash] = React.useState<string | null>(null);
  const [rewardExpiresAt, setRewardExpiresAt] = React.useState<number | null>(null);
  const [isGameLoaded, setIsGameLoaded] = React.useState(false);
  const rewardAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const playRewardSound = React.useCallback(() => {
    if (!rewardAudioRef.current) {
      rewardAudioRef.current = new Audio(RewardSound);
    }
    const audio = rewardAudioRef.current;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, []);

  // useLayoutEffect chạy đồng bộ TRƯỚC khi trình duyệt paint (khác useEffect
  // chạy sau paint) — tránh cảnh trang paint sai kích thước theo CSS rồi mới
  // giật về đúng kích thước theo JS.
  React.useLayoutEffect(() => {
    const el = document.documentElement;
    const update = () => {
      const wRef = window.innerWidth < 768 ? 1070 : 1536;
      el.style.fontSize = `${Math.min(window.innerWidth / wRef, window.innerHeight / 1920) * 16}px`;
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      el.style.fontSize = "";
    };
  }, []);

  React.useEffect(() => {
    const tryGetLandingXDLive = async (apiDomain: string) => {
      try {
        await getDeviceFingerprint();
        const client = createMyEventClient(apiDomain, getToken(), true);
        const reply = await client.getLandingXDLive({});
        const reward = reply.reward;
        const expSeconds = reward?.expiresAt?.seconds;
        const now = Math.floor(Date.now() / 1000);
        const hasClaimableReward =
          !!reward &&
          reward.status === LandingXDLiveStatus.LANDING_XD_LIVE_UNCLAIMED &&
          reward.cash > BigInt(0) &&
          (!expSeconds || Number(expSeconds) > now);

        if (hasClaimableReward) {
          setRewardCash(String(reward.cash / BigInt(1000)));
          const rounds = reply.snapshot?.rounds ?? [];
          const totalNet = rounds.reduce(
            (sum, r) => sum + (r.net ?? BigInt(0)),
            BigInt(0),
          );
          const finalBalanceRaw = BigInt(STARTING_BALANCE_RAW) + totalNet;
          const finalBalance = finalBalanceRaw / BigInt(1000);
          setFinalBalanceCash(finalBalance > BigInt(0) ? finalBalance.toString() : "0");
          if (expSeconds) setRewardExpiresAt(Number(expSeconds));
          playRewardSound();
          setIsRewardModalOpen(true);
        }
      } catch {
        // ignore — user sees normal intro
      }
    };

    const bootstrap = useMyStore.getState().bootstrap;
    if (bootstrap?.apiDomain) {
      tryGetLandingXDLive(bootstrap.apiDomain);
      return;
    }
    const unsubscribe = useMyStore.subscribe((state) => {
      if (state.bootstrap?.apiDomain) {
        unsubscribe();
        tryGetLandingXDLive(state.bootstrap.apiDomain);
      }
    });
    return unsubscribe;
  }, []);


  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null,
  );
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedFormStartRef = React.useRef(false);

  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);

  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    },
    [],
  );

  const trackFormStart = React.useCallback(() => {
    if (hasTrackedFormStartRef.current) return;
    hasTrackedFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_xocdia_wizard" });
  }, [pushDataLayerEvent]);

  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleRegisterSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_xocdia_wizard",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({ app_id: INTERCOM_APP_ID });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }
    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  React.useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.command === "ON_PREPLAY_UNCLAIMED") {
        const cash = e.data?.reward?.cash;
        if (cash) setRewardCash(String(Math.floor(parseFloat(cash) / 1000)));

        const rounds: { net?: string }[] = e.data?.snapshot?.rounds ?? [];
        const totalNet = rounds.reduce((sum, r) => sum + parseFloat(r.net ?? "0"), 0);
        const finalBalance = STARTING_BALANCE_RAW + totalNet;
        setFinalBalanceCash(String(Math.max(0, Math.floor(finalBalance / 1000))));

        const expSeconds = e.data?.reward?.expiresAt?.seconds;
        if (expSeconds) setRewardExpiresAt(Number(expSeconds));

        playRewardSound();
        setIsRewardModalOpen(true);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Đếm ngược đến expiresAt (hoặc fallback 24h) khi modal bước 3 đang mở.
  React.useEffect(() => {
    if (!isRewardModalOpen) return;
    const getSecondsLeft = () => {
      if (rewardExpiresAt) {
        return Math.max(0, rewardExpiresAt - Math.floor(Date.now() / 1000));
      }
      return claimSecondsLeft;
    };
    setClaimSecondsLeft(getSecondsLeft());
    const timer = window.setInterval(() => {
      setClaimSecondsLeft(getSecondsLeft());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isRewardModalOpen, rewardExpiresAt]);

  const handlePlayClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_cta_play", { section: "hero" });
    if (!rewardAudioRef.current) {
      rewardAudioRef.current = new Audio(RewardSound);
    }
    setStep("playing");
  }, [pushDataLayerEvent]);

  const handleCloseRewardModal = React.useCallback(() => {
    setIsRewardModalOpen(false);
  }, []);

  const handleOpenRegisterModal = React.useCallback(() => {
    pushDataLayerEvent("lp_click_claim_reward", { section: "reward_modal" });
    setIsRewardModalOpen(false);
    setIsRegisterModalOpen(true);
  }, [pushDataLayerEvent]);

  const handleCloseRegisterModal = React.useCallback(() => {
    setIsRegisterModalOpen(false);
  }, []);

  const handleSocialClick = React.useCallback(
    (platformId: string, url: string) => {
      const eventName =
        platformId === "telegram"
          ? "lp_click_social_Telegram"
          : "lp_click_social_FB";
      pushDataLayerEvent(eventName, { social_platform: platformId, location: "footer" });
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [pushDataLayerEvent],
  );

  return (
    <div className={styles.home}>
      {/* === Ground layer: dùng chung cho mọi bước === */}
      <img
        className={styles.heroBackground}
        src={BackgroundDesktop}
        alt=""
        aria-hidden="true"
      />

      <div className={styles.header}>
        <img src={LogoWinaz} alt="WinAZ" className={styles.logo} />
        <div className={styles.tickerViewport}>
          <Marquee speed={50}>
            {TICKER_ITEMS.map((text, i) => (
              <span key={i} className={styles.tickerItem}>
                {text.split(/(WINAZ)/g).map((part, j) =>
                  part === "WINAZ" ? (
                    <strong key={j} className={styles.tickerHighlight}>{part}</strong>
                  ) : part
                )}
              </span>
            ))}
          </Marquee>
        </div>
      </div>

      {/* Header riêng cho mobile */}
      <div className={styles.mobileHeader}>
        <img src={LogoWinaz} alt="WinAZ" className={styles.mobileHeaderLogo} />
        <div className={styles.mobileTickerViewport}>
          <Marquee speed={40}>
            {TICKER_ITEMS.map((text, i) => (
              <span key={i} className={styles.tickerItem}>
                {text.split(/(WINAZ)/g).map((part, j) =>
                  part === "WINAZ" ? (
                    <strong key={j} className={styles.tickerHighlight}>{part}</strong>
                  ) : part
                )}
              </span>
            ))}
          </Marquee>
        </div>
      </div>

      <img
        src={Lantern}
        alt=""
        aria-hidden="true"
        className={styles.lanternLeft}
      />
      <img
        src={Lantern}
        alt=""
        aria-hidden="true"
        className={styles.lanternRight}
      />

      <div className={styles.canvas}>
        <img
          src={SideLabelLeft}
          alt={SIDE_LABEL_LEFT}
          className={`${styles.sideLabel} ${styles.sideLabelLeft}`}
        />
        <img
          src={SideLabelRight}
          alt={SIDE_LABEL_RIGHT}
          className={`${styles.sideLabel} ${styles.sideLabelRight}`}
        />
        <div
          className={
            step === "intro"
              ? styles.headlineRow
              : `${styles.headlineRow} ${styles.headlineRowGameStage}`
          }
        >
          {step === "intro" ? (
            <>
              <img
                src={HeadlineImg}
                alt={`${GAME_LABEL_PREFIX} ${GAME_LABEL_VALUE}`}
                className={styles.headlineImg}
              />
            </>
          ) : (
            <img
              src={GameStageHeadline}
              alt={GAME_STAGE_HEADLINE_ALT}
              className={styles.gameStageHeadlineImg}
            />
          )}
        </div>

        {step === "intro" ? (
          <>
            <div className={styles.gameLabelBadge}>
              <img
                src={GameLabelBg}
                alt=""
                aria-hidden="true"
                className={styles.gameLabelBadgeImg}
              />
              <span className={styles.gameLabelBadgeText}>
                <span className={styles.gameLabelBadgePrefix}>{GAME_LABEL_PREFIX}</span>
                <span className={styles.gameLabelBadgeValue}>{GAME_LABEL_VALUE}</span>
              </span>
            </div>

            <div className={styles.heroFrame}>
              <img src={HeroImg} alt="Xóc đĩa live" className={styles.heroImg} />
            </div>

            <div
              className={styles.liveNoticeBar}
              style={{ ["--live-notice-bg" as any]: `url(${LiveNoticeBg})` }}
            >
              <img src={LiveBadge} alt="LIVE" className={styles.liveBadge} />
              <span>{LIVE_NOTICE_TEXT}</span>
            </div>

            <button
              type="button"
              className={styles.ctaButtonWrapper}
              onClick={handlePlayClick}
              {...INTERACTIVE_IMAGE_PROPS}
            >
              <video
                src={CtaPlayButton}
                className={styles.ctaButtonImg}
                autoPlay
                loop
                muted
                playsInline
              />
            </button>

            <div className={styles.subtext}>
              <div>{SUBTEXT_LINE_1}</div>
              <div>
                {SUBTEXT_LINE_2_PREFIX}
                <strong>{SUBTEXT_LINE_2_HIGHLIGHT}</strong>
              </div>
            </div>

            <div className={styles.featureRow}>
              {[FeatureCard1, FeatureCard2, FeatureCard3].map((src, index) => (
                <div key={FEATURE_CARDS_ALT[index]} className={styles.featureCard}>
                  <img src={src} alt={FEATURE_CARDS_ALT[index]} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={styles.gameStage}>
            </div>
            <div className={styles.gameFrame}>
              <iframe
                src={GAME_IFRAME_URL}
                className={styles.gameIframe}
                title="Xóc Đĩa Live"
                allow="autoplay; fullscreen"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onLoad={() => setIsGameLoaded(true)}
                style={{ opacity: isGameLoaded ? 1 : 0, transition: "opacity 0.3s ease" }}
              />
            </div>

            <div
              className={styles.onlineBadge}
              style={{ ["--online-badge-bg" as any]: `url(${OnlineBadgeBg})` }}
            >
              <span>
                {onlineCount.toLocaleString("vi-VN")} {ONLINE_BADGE_SUFFIX}
              </span>
            </div>

            {activeProof ? (
              <div
                className={`${styles.proofBadge} ${
                  activeProof.exiting ? styles.proofBadgeExiting : ""
                }`}
                style={{ ["--proof-badge-bg" as any]: `url(${ProofBadgeBg})` }}
              >
                <p className={styles.proofBadgeLine}>
                  Người chơi <strong>{activeProof.item.username}</strong>
                </p>
                <p className={styles.proofBadgeLine}>
                  Vừa thắng lớn <strong>{activeProof.item.prize} WIN</strong>
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* === Bước 3: modal chúc mừng trúng thưởng === */}
        {isRewardModalOpen ? (
          <div className={styles.modalOverlay}>
            <div className={styles.rewardModal}>
              <img src={ScrollBg} alt="" aria-hidden="true" className={styles.rewardModalBg} />
              <h2 className={styles.rewardTitle}>{REWARD_TITLE}</h2>
              <p className={styles.rewardDescription}>
                Kết thúc trải nghiệm! Số dư cuối: {formatCash(finalBalanceCash ?? "0")}.
                <br />
                Hệ thống xin tặng quý khách phần thưởng:
              </p>

              <div className={styles.rewardItem}>
                <div
                  className={styles.rewardItemIconBox}
                  style={{ ["--reward-item-icon-bg" as any]: `url(${GiftIconBg})` }}
                >
                  <img src={GiftIcon} alt="" aria-hidden="true" className={styles.rewardItemIcon} />
                </div>
                <div className={styles.rewardItemText}>
                  <strong>Tặng ngay {formatCash(rewardCash)}</strong>
                  <span>{REWARD_ITEM_SUBLABEL}</span>
                </div>
              </div>

              <p className={styles.rewardCountdown}>
                Quý khách có 24 giờ để nhận thưởng.
                <br />
                Phần quà còn có giá trị trong{" "}
                <strong>{formatCountdown(claimSecondsLeft)}</strong>
              </p>

              <button
                type="button"
                className={styles.rewardClaimButton}
                onClick={handleOpenRegisterModal}
                {...INTERACTIVE_IMAGE_PROPS}
              >
                <img src={BtnNhanThuong} alt="Nhận thưởng" />
              </button>
            </div>
          </div>
        ) : null}

        {/* === Bước 4: modal form đăng ký === */}
        {isRegisterModalOpen ? (
          <div className={styles.modalOverlay}>
            <div className={styles.registerModal}>
            <img
              src={LogoWinaz}
              alt="WinAZ"
              className={styles.registerModalLogo}
            />

            <form className={styles.registerForm} onSubmit={handleRegisterSubmit}>
              <div className={styles.fieldGroup}>
                <label
                  className={styles.inputWrapper}
                  style={{ ["--input-bg" as any]: `url(${InputBg})` }}
                >
                  <img src={IconUser} alt="" aria-hidden="true" className={styles.inputIcon} />
                  <input
                    type="text"
                    placeholder="Tên đăng nhập"
                    value={username}
                    onChange={handleUsernameChange}
                    onFocus={trackFormStart}
                    disabled={isSubmitting}
                  />
                </label>
                {usernameError ? (
                  <div className={styles.inlineError} aria-live="assertive">
                    Vui lòng nhập tên đăng nhập
                  </div>
                ) : null}
              </div>

              <div className={styles.fieldGroup}>
                <label
                  className={styles.inputWrapper}
                  style={{ ["--input-bg" as any]: `url(${InputBg})` }}
                >
                  <img src={IconPassword} alt="" aria-hidden="true" className={styles.inputIcon} />
                  <input
                    type="password"
                    placeholder="Mật khẩu"
                    value={password}
                    onChange={handlePasswordChange}
                    onFocus={trackFormStart}
                    disabled={isSubmitting}
                  />
                </label>
                {passwordError ? (
                  <div className={styles.inlineError} aria-live="assertive">
                    Vui lòng nhập mật khẩu
                  </div>
                ) : null}
              </div>

              {turnstileRequired ? (
                <div className={styles.turnstileGroup}>
                  <div className={styles.turnstileBlock}>
                    <div className={styles.turnstileInner}>
                      <TurnstileWidget
                        ref={turnstileWidgetRef}
                        className={styles.turnstileWidget}
                        onTokenChange={handleTurnstileTokenChange}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {apiError ? (
                <div className={styles.inlineError} aria-live="assertive">
                  {apiError}
                </div>
              ) : null}

              <button
                type="submit"
                onClick={() =>
                  pushDataLayerEvent("lp_click_cta_register", {
                    location: "hero_desktop",
                  })
                }
                className={styles.registerSubmitButton}
                disabled={isSubmitting}
                {...INTERACTIVE_IMAGE_PROPS}
                data-cta
              >
                <img src={BtnNhanThuong} alt="Nhận thưởng" />
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {/* === Footer: dùng chung mọi bước === */}
      <div className={styles.footer}>
        <img
          src={LogoGalaxy}
          alt="Galaxy Entertainment Group"
          className={styles.footerGalaxyLogo}
        />
        <span className={styles.footerCopyright}>
          Copyright 2025 Galaxy Entertainment Group Limited.
        </span>
        <div className={styles.footerSocial}>
          <button
            type="button"
            className={styles.footerSocialLink}
            onClick={() => handleSocialClick("facebook", SOCIAL_LINKS.facebook)}
            aria-label="Facebook"
          >
            <img src={IconFacebook} alt="Facebook" />
          </button>
          <button
            type="button"
            className={styles.footerSocialLink}
            onClick={() => handleSocialClick("telegram", SOCIAL_LINKS.telegram)}
            aria-label="Telegram"
          >
            <img src={IconTelegram} alt="Telegram" />
          </button>
        </div>
      </div>

      {/* Footer riêng cho mobile */}
      <div className={styles.mobileFooter}>
        <img src={LogoGalaxy} alt="Galaxy Entertainment Group" className={styles.mobileFooterLogo} />
        <span className={styles.mobileFooterCopyright}>Copyright 2025 Galaxy Entertainment Group Limited.</span>
        <div className={styles.footerSocial}>
          <button type="button" className={styles.footerSocialLink} onClick={() => handleSocialClick("facebook", SOCIAL_LINKS.facebook)} aria-label="Facebook">
            <img src={IconFacebook} alt="Facebook" />
          </button>
          <button type="button" className={styles.footerSocialLink} onClick={() => handleSocialClick("telegram", SOCIAL_LINKS.telegram)} aria-label="Telegram">
            <img src={IconTelegram} alt="Telegram" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
